import { useEffect, useState, type ReactNode } from 'react'
import { AlertDialog, Button, Chip, Input, Switch } from '@heroui/react'
import { api, type StoredLayout } from '../lib/api'
import { listState } from '../lib/listState'
import { nameIn } from '../lib/staffText'
import type { DeliveredService, Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { DeliveredTree } from './Delivered'
import { Loading } from './Loading'
import { Check, Messages, Names, Num, Text, activeFirst, useAction } from './parts'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, PageHead, RowArrow, toneOf, useOpenRecord } from './settingsKit'
import { EmptyState } from '../ui/parts'
import { useMayRead, useSession } from '../ui/Session'
import { optionOn } from '../lib/orgOptions'
import '../styles/branch.css'
import { Pick as SetupPick } from './setupKit'
import { useCustomerLanguages } from '../lib/i18n/useCustomerLanguages'
import { languageNames, missingLanguages } from '../lib/i18n/missing'
import { MissingMark } from '../ui/MissingMark'

/**
 * Services: what the customer came to do.
 *
 * A service belongs to the organisation rather than to a branch. "Cash" means the
 * same thing in every branch of a bank; which queues take it, with their letters,
 * is set in the operation profiles.
 *
 * Under each service, in its panel, is the tree of what a desk may say it
 * delivered within it — see Delivered.tsx.
 *
 * Delete removes a service history never named and archives any other; the
 * Archived chip lists those, with Restore.
 */

const blank = (): Partial<Service> => ({
  code: '',
  name: {},
  avgServiceSec: 300,
  sortOrder: 0,
  active: true,
})

export function Services({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const handoffOn = optionOn(useSession()?.identity, 'handoff')
  const [services, setServices] = useState<Service[]>([])
  const [delivered, setDelivered] = useState<DeliveredService[]>([])
  const [find, setFind] = useState('')
  const [kind, setKind] = useState<'all' | 'on' | 'off' | 'archived' | 'missing'>('all')
  const customerLanguages = useCustomerLanguages(organisation)
  const missingOf = (s: Pick<Service, 'name'>) => missingLanguages(s.name, customerLanguages)
  const [deleting, setDeleting] = useState<Service | null>(null)
  // False until the first answer, so a slow line reads as Loading, not as empty.
  const [loaded, setLoaded] = useState(false)
  // The same for the tree of delivered services, which is read apart from the list.
  const [treeLoaded, setTreeLoaded] = useState(false)

  // The organisation's ticket designs. A failure is not worth a red line: the
  // chooser then offers only the branch's slip, which is what it says.
  const [tickets, setTickets] = useState<StoredLayout[]>([])
  const readsLayouts = useMayRead('layouts')
  useEffect(() => {
    if (!readsLayouts) return
    api
      .layouts(organisation)
      .then((r) => setTickets(r.layouts.filter((l) => l.kind === 'ticket')))
      .catch(() => setTickets([]))
  }, [organisation, readsLayouts])

  const load = async () => {
    // The tree is caught on its own: a failure to read it must not turn a service
    // that saved into a red line, nor leave the services list stale.
    const [listed, tree] = await Promise.all([
      api.catalogueServices(organisation, true),
      api.deliveredServices(organisation).catch(() => undefined),
    ])
    setServices(listed.services)
    if (tree) setDelivered(tree.deliveredServices)
    setLoaded(true)
    setTreeLoaded(true)
  }
  const { busy, error, notice, run, setError, setNotice } = useAction(load)

  // The toast says what the server did: deleted, or archived because history names it.
  const remove = async (s: Service) => {
    let result = ''
    await run(async () => {
      result = (await api.deleteService(s.id)).result
    }, '')
    setDeleting(null)
    if (!result) return
    if (rec.row?.id === s.id) rec.close()
    setNotice(say(result === 'archived' ? 'catalogue.services.archived' : 'catalogue.services.deleted'))
  }
  const restore = (s: Service) => run(() => api.restoreService(s.id), say('catalogue.services.restored'))

  useEffect(() => {
    api
      .catalogueServices(organisation, true)
      .then((listed) => {
        setServices(listed.services)
        setLoaded(true)
      })
      .catch(() => {
        setError(say('catalogue.services.notLoaded'))
        setLoaded(true)
      })
    // Apart from the services, so that a server without the tree still shows the
    // services it can.
    api
      .deliveredServices(organisation)
      .then((tree) => setDelivered(tree.deliveredServices))
      .catch(() => setDelivered([]))
      .finally(() => setTreeLoaded(true))
  }, [organisation, setError, say])

  const f = find.trim().toLocaleLowerCase()
  const live = services.filter((s) => !s.archivedAt)
  const archived = services.filter((s) => s.archivedAt)
  const shown = activeFirst(kind === 'archived' ? archived : live, (s) => s.active).filter(
    (s) => !f || `${nameIn(s.name, lang, '')} ${s.code}`.toLocaleLowerCase().includes(f),
  )
  // The service open on its own page (#services/<id>, #services/new), as the list now
  // has it: the delivered tree under it is saved row by row and read back into this list.
  const rec = useOpenRecord(services, (s) => s.id, loaded)
  const open = rec.row
  const editing = rec.isNew ? blank() : open

  // A save that leaves a customer language without the name says so; it never refuses.
  const savedLine = (draft: Partial<Service>, line: string) => {
    const miss = missingOf({ name: draft.name ?? {} })
    return miss.length > 0 ? say('content.savedMissing', { langs: languageNames(miss) }) : line
  }
  const offered = live.filter((s) => s.active).length
  const untranslated = live.filter((s) => missingOf(s).length > 0).length
  const listed = shown.filter((s) =>
    kind === 'missing' ? missingOf(s).length > 0 : kind === 'all' || kind === 'archived' || (kind === 'on') === s.active,
  )

  const confirm = (
    <AlertDialog.Backdrop isOpen={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
      <AlertDialog.Container size="sm">
        <AlertDialog.Dialog>
          <AlertDialog.Header>
            <AlertDialog.Icon status="danger" />
            <AlertDialog.Heading>
              {say('catalogue.services.deleteAsk', { name: deleting ? nameIn(deleting.name, lang, deleting.code) : '' })}
            </AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Footer>
            <Button variant="tertiary" onPress={() => setDeleting(null)}>
              {say('common.cancel')}
            </Button>
            <Button variant="danger" isDisabled={busy} onPress={() => deleting && void remove(deleting)}>
              {say('catalogue.services.delete')}
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  )

  if (editing) {
    return (
      <>
        <ServiceSheet
          key={rec.code}
          service={editing}
          saved={open}
          tickets={tickets}
          busy={busy}
          error={error}
          onClose={() => {
            rec.close()
            setError('')
          }}
          onDelete={open && !open.archivedAt ? () => setDeleting(open) : undefined}
          onSave={(draft) =>
            editing.id
              ? run(async () => {
                  await api.updateService(editing.id!, draft)
                  rec.close()
                }, savedLine(draft, say('catalogue.services.saved', { name: nameIn(draft.name, lang, draft.code ?? '') })))
              : run(async () => {
                  await api.createService({ ...draft, ...(organisation ? { organisation } : {}) })
                  rec.close()
                }, savedLine(draft, say('catalogue.services.added')))
          }
        >
          {open && (
            <DeliveredTree
              service={open}
              rows={delivered.filter((d) => d.serviceId === open.id)}
              loaded={treeLoaded}
              busy={busy}
              run={run}
            />
          )}
        </ServiceSheet>
        {confirm}
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.services')}
        line={say('settings.services.line')}
        actions={
          <Button variant="primary" onPress={() => rec.open()}>
            <Icon name="plus" size={16} /> {say('catalogue.services.add')}
          </Button>
        }
      />
      <Messages error={error} notice={notice} />

      <ListCard
        title={say('admin.section.services')}
        count={loaded ? say('settings.services.count', { n: live.length }) : undefined}
        search={find}
        onSearch={setFind}
        searchLabel={say('catalogue.services.find')}
        chips={[
          { value: 'all', label: say('settings.all') },
          { value: 'on', label: say('catalogue.services.offered'), count: offered },
          { value: 'off', label: say('settings.services.off'), count: live.length - offered },
          ...(untranslated > 0 || kind === 'missing'
            ? [{ value: 'missing' as const, label: say('content.untranslated'), count: untranslated }]
            : []),
          ...(archived.length > 0 || kind === 'archived'
            ? [{ value: 'archived' as const, label: say('catalogue.services.archive'), count: archived.length }]
            : []),
        ]}
        chip={kind}
        onChip={setKind}
      >
        {listState(loaded, live.length) === 'loading' && <Loading />}
        {listState(loaded, live.length) === 'empty' && kind !== 'archived' && (
          <EmptyState icon={<Icon name="grid" size={20} />} title={say('catalogue.services.nothingOffered')} />
        )}
        {services.length > 0 && listed.length === 0 && find.trim() !== '' && (
          <EmptyState title={say('catalogue.services.noMatch', { q: find })} />
        )}
        {listed.length > 0 && (
          <ListTable
            label={say('admin.section.services')}
            cols="minmax(220px, 1.6fr) 110px 96px 40px 16px"
            head={[
              say('catalogue.services.service'),
              say('catalogue.services.usual'),
              kind === 'archived' ? '' : say('catalogue.services.offered'),
              '',
              '',
            ]}
          >
            {listed.map((s) => {
              const name = nameIn(s.name, lang, s.code)
              if (s.archivedAt) {
                return (
                  <ListRow key={s.id} off>
                    <NameCell tile={<IconTile text={s.code.slice(0, 2)} tone={toneOf(s.id)} />} name={name} sub={s.code} />
                    <span role="cell" className="set-num">
                      {say('catalogue.minutes', { n: Math.round(s.avgServiceSec / 60) })}
                    </span>
                    <span role="cell">
                      <Button size="sm" variant="secondary" isDisabled={busy} aria-label={say('catalogue.services.restoreName', { name })} onPress={() => void restore(s)}>
                        {say('catalogue.services.restore')}
                      </Button>
                    </span>
                    <span role="cell" />
                    <span />
                  </ListRow>
                )
              }
              return (
                <ListRow key={s.id} actions off={!s.active} onOpen={() => rec.open(s)}>
                  <NameCell
                    tile={<IconTile text={s.code.slice(0, 2)} tone={toneOf(s.id)} />}
                    name={
                      <>
                        {name}
                        <MissingMark codes={missingOf(s)} />
                        {handoffOn && s.cashier && (
                          <Chip size="sm" variant="secondary">
                            {say('catalogue.services.cashier')}
                          </Chip>
                        )}
                      </>
                    }
                    sub={s.code}
                  />
                  <span role="cell" className="set-num">
                    {say('catalogue.minutes', { n: Math.round(s.avgServiceSec / 60) })}
                  </span>
                  <span role="cell">
                    <Switch
                      isSelected={s.active}
                      isDisabled={busy}
                      aria-label={say('catalogue.services.offer', { name })}
                      onChange={() =>
                        run(
                          () => api.updateService(s.id, { ...s, active: !s.active }),
                          s.active ? say('catalogue.services.nowOff', { name }) : say('catalogue.services.nowOn', { name }),
                        )
                      }
                    >
                      <Switch.Content>
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Content>
                    </Switch>
                  </span>
                  <span role="cell">
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      isDisabled={busy}
                      aria-label={say('catalogue.services.deleteName', { name })}
                      onPress={() => setDeleting(s)}
                    >
                      <Icon name="trash" size={17} />
                    </Button>
                  </span>
                  <RowArrow />
                </ListRow>
              )
            })}
          </ListTable>
        )}
      </ListCard>
      {confirm}
    </>
  )
}

function ServiceSheet({
  service,
  saved,
  tickets,
  busy,
  error,
  onClose,
  onSave,
  onDelete,
  children,
}: {
  service: Partial<Service>
  saved?: Service
  tickets: StoredLayout[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (draft: Partial<Service>) => void
  onDelete?: () => void
  children?: ReactNode
}) {
  const handoffOn = optionOn(useSession()?.identity, 'handoff')
  const { lang, say } = useStaffText()
  const [draft, setDraft] = useState<Partial<Service>>(service)
  const isNew = !service.id
  const set = (patch: Partial<Service>) => setDraft((d) => ({ ...d, ...patch }))
  const valid = Boolean(draft.code?.trim()) && JSON.stringify(draft) !== JSON.stringify(service)
  // Minutes on screen, seconds on the wire; a field untouched keeps its seconds.
  const minutes = (sec: number | undefined) => Math.round((sec ?? 0) / 60)
  const toSec = (typed: string) => Math.max(0, Math.round(Number(typed) || 0)) * 60

  return (
    <InnerPage
      back={say('admin.section.services')}
      title={isNew ? say('catalogue.services.new') : nameIn(service.name, lang, service.code ?? '')}
      onClose={onClose}
      onSubmit={() => onSave(draft)}
      submitLabel={isNew ? say('catalogue.services.add') : say('catalogue.services.save')}
      valid={valid}
      busy={busy}
      error={error}
      wide={!isNew}
      extra={
        onDelete && (
          <Button variant="danger-soft" isDisabled={busy} onPress={onDelete}>
            {say('catalogue.services.delete')}
          </Button>
        )
      }
    >
      <Names value={draft.name ?? {}} onChange={(name) => set({ name })} />
      <div className="branch-pair admin-top">
        <label className="field">
          <span title={say('catalogue.services.codeHint')}>{say('common.code')}</span>
          <Text uppercase value={draft.code ?? ''} onChange={(code) => set({ code })} />
        </label>
        <label className="field">
          <span title={say('catalogue.services.handlingHint')}>{say('catalogue.services.usualMin')}</span>
          <Input
            fullWidth
            type="number"
            min={0}
            value={minutes(draft.avgServiceSec)}
            onChange={(e) => set({ avgServiceSec: toSec(e.target.value) })}
          />
        </label>
      </div>

      <label className="field">
        <span title={say('catalogue.services.limitHint')}>{say('catalogue.services.limitMin')}</span>
        <Input
          className="admin-num"
          type="number"
          min={0}
          max={480}
          value={draft.serviceTimeLimitMin ?? 0}
          onChange={(e) => set({ serviceTimeLimitMin: Math.max(0, Math.min(480, Math.round(Number(e.target.value) || 0))) })}
        />
      </label>

      {!isNew && (
        <>
          <label className="field">
            <span title={say('catalogue.lowerFirst')}>{say('catalogue.services.kioskOrder')}</span>
            <Num value={draft.sortOrder ?? 0} onChange={(sortOrder) => set({ sortOrder })} />
          </label>
          {/* Which slip this service prints. Optional: empty prints what the
              branch prints, which is almost every service. A kiosk with a slip of
              its own still prints that — the lounge keeps its paper. */}
          <label className="field">
            <span>{say('admin.branches.printedTicket')}</span>
            <SetupPick
              label={say('admin.branches.printedTicket')}
              value={draft.ticketLayoutId ?? ''}
              onChange={(ticketLayoutId) => set({ ticketLayoutId })}
              options={[
                { value: '', label: say('catalogue.services.whatBranchPrints') },
                ...tickets.map((layout) => ({
                  value: layout.id,
                  label: layout.published
                    ? layout.name || say('admin.branches.unnamedTicket')
                    : say('admin.branches.notPublishedYet', { name: layout.name || say('admin.branches.unnamedTicket') }),
                })),
              ]}
            />
          </label>
          <Check
            label={say('catalogue.services.offered')}
            checked={draft.active ?? true}
            onChange={(active) => set({ active })}
            hint={say('catalogue.services.offeredHint')}
          />
          {handoffOn && (
            <Check
              label={say('catalogue.services.cashier')}
              checked={draft.cashier ?? false}
              onChange={(cashier) => set({ cashier })}
              hint={say('catalogue.services.cashierHint')}
            />
          )}
          {saved && children}
        </>
      )}
    </InnerPage>
  )
}
